import { createContext, useContext } from 'react';
import { commentService } from '../../clients/ApiClients';

/**
 * What a comment thread hangs off (#616).
 *
 * The two share one thread implementation, and one difference that
 * matters: **who the shared thread reaches.** On a violation it is the
 * board and the cited homeowner. On a work order it is the board only —
 * work orders have no homeowner party, and the member portal never serves
 * one. Everything the UI says about a thread is keyed on this, so a
 * manager is never told a work-order note will reach a homeowner, or a
 * violation reply that it will not.
 *
 * A reservation (#640 phase 2) is the third: its shared thread reaches the
 * board and **every member of the booking's household** — the home, not
 * one homeowner, because the booking belongs to the home.
 */
export type CommentSubjectKind = 'violation' | 'work_order' | 'reservation';

export interface CommentSubject {
  kind: CommentSubjectKind;
  id: string;
}

/**
 * The subject half of a create payload: **exactly one** of the three ids.
 * The server answers more than one, or none, with a 400.
 */
export const subjectField = (
  subject: CommentSubject
): { violation_id: string } | { work_order_id: string } | { reservation_id: string } => {
  switch (subject.kind) {
    case 'violation':
      return { violation_id: subject.id };
    case 'work_order':
      return { work_order_id: subject.id };
    case 'reservation':
      return { reservation_id: subject.id };
  }
};

/** Every comment on a subject that this caller may read. */
export const fetchComments = (subject: CommentSubject) => {
  switch (subject.kind) {
    case 'violation':
      return commentService.getCommentsByViolation({ violationId: subject.id });
    case 'work_order':
      return commentService.getCommentsByWorkOrder({ workOrderId: subject.id });
    case 'reservation':
      return commentService.getCommentsByReservation({ reservationId: subject.id });
  }
};

/**
 * The subject kind of the thread being rendered, for the pieces below it
 * — composer, comment — that phrase the audience.
 *
 * Defaults to `violation`, which is what every thread was before #616, so
 * a composer rendered on its own keeps saying what it always said.
 */
export const CommentSubjectKindContext =
  createContext<CommentSubjectKind>('violation');

export const useCommentSubjectKind = (): CommentSubjectKind =>
  useContext(CommentSubjectKindContext);
